/**
 * Webtech HR Engine™ - Dashboard Page
 * Version 2.0 - Expérience RH intelligente et guidée
 * Design moderne inspiré d'Odoo + Notion + Analytics
 */

import { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import api from '../lib/api';
import { toast } from 'sonner';
import OnboardingWizard from '../components/onboarding/OnboardingWizard';
import KPIDashboard from '../components/KPIDashboard';
import { 
  Users, 
  Building2, 
  MapPin, 
  UserCog,
  Upload,
  Network,
  BookUser,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Settings,
  TrendingUp,
  TrendingDown,
  Star,
  Brain,
  Calendar,
  Clock,
  Target,
  Award,
  FileText,
  Share2,
  Printer,
  Download,
  MoreHorizontal,
  Plus,
  Activity,
  PieChart,
  UserPlus,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Sparkles,
  Zap,
  ChevronRight,
  Crown,
  Lock,
  LineChart,
  BarChart3,
  Shield,
  Lightbulb,
  CircleCheck,
  Circle,
  Eye,
  FileBarChart,
  ClipboardList,
  Play,
  Briefcase,
  BookOpen
} from 'lucide-react';

// Helper to get greeting based on time
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bonjour';
  if (hour < 18) return 'Bon après-midi';
  return 'Bonsoir';
};

// Helper to get day name in French
const getDayName = () => {
  const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  return days[new Date().getDay()];
};

// Helper to format date in French
const formatDate = () => {
  return new Date().toLocaleDateString('fr-FR', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
};

// Semantic label helper
const getSemanticLabel = (type, value, total) => {
  if (type === 'employees') {
    if (value === 0) return { label: 'À configurer', color: 'text-amber-600', bg: 'bg-amber-50' };
    if (value < 5) return { label: 'Équipe réduite', color: 'text-blue-600', bg: 'bg-blue-50' };
    if (value < 20) return { label: 'Équipe stable', color: 'text-green-600', bg: 'bg-green-50' };
    return { label: 'Grande équipe', color: 'text-purple-600', bg: 'bg-purple-50' };
  }
  if (type === 'coverage') {
    if (value === 100) return { label: 'Complet ✓', color: 'text-green-600', bg: 'bg-green-50' };
    if (value >= 75) return { label: 'Bon niveau', color: 'text-blue-600', bg: 'bg-blue-50' };
    if (value >= 50) return { label: 'En progrès', color: 'text-amber-600', bg: 'bg-amber-50' };
    return { label: 'À compléter', color: 'text-red-600', bg: 'bg-red-50' };
  }
  if (type === 'health') {
    if (value >= 70) return { label: 'Excellent', color: 'text-green-600', bg: 'bg-green-50', icon: '🟢' };
    if (value >= 50) return { label: 'Correct', color: 'text-blue-600', bg: 'bg-blue-50', icon: '🔵' };
    if (value >= 30) return { label: 'À surveiller', color: 'text-amber-600', bg: 'bg-amber-50', icon: '🟡' };
    return { label: 'Critique', color: 'text-red-600', bg: 'bg-red-50', icon: '🔴' };
  }
  return { label: '', color: '', bg: '' };
};

// PRO Upgrade Modal Component
function ProUpgradeModal({ isOpen, onClose, feature }) {
  const navigate = useNavigate();
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 text-center">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-webtech-yellow to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
            <Crown className="w-8 h-8 text-white" />
          </div>
          
          {/* Title */}
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            Fonctionnalité PRO
          </h2>
          
          {/* Description */}
          <p className="text-slate-600 mb-6">
            <strong className="text-webtech-blue">{feature}</strong> est disponible avec le plan PRO.
          </p>
          
          {/* Benefits */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm font-medium text-slate-700 mb-3">Avec le plan PRO, débloquez :</p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Analyse IA et recommandations
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Campagnes d'évaluation avancées
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Gestion des compétences complète
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Reporting et tableaux de bord
              </li>
            </ul>
          </div>
          
          {/* CTA */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Plus tard
            </Button>
            <Button 
              className="flex-1 bg-gradient-to-r from-webtech-blue to-blue-600 hover:from-webtech-blue-hover hover:to-blue-700"
              onClick={() => {
                onClose();
                navigate('/billing/subscription');
              }}
            >
              <Crown className="w-4 h-4 mr-2" />
              Passer au PRO
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// AI Recommendations Card Component
function AIRecommendationsCard({ aiData, isPro, onUpgradeClick }) {
  const navigate = useNavigate();
  
  if (!isPro) {
    return (
      <Card className="border-2 border-dashed border-slate-300 bg-gradient-to-br from-slate-50 to-slate-100">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-200 flex items-center justify-center">
              <Lock className="w-6 h-6 text-slate-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-slate-600">Recommandations IA</h3>
                <Badge className="bg-webtech-yellow text-white text-xs">BUSINESS</Badge>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                L'IA analyse vos données RH et vous dit exactement quoi faire.
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onUpgradeClick('Recommandations IA')}
                className="border-webtech-blue text-webtech-blue hover:bg-webtech-blue hover:text-white"
              >
                <Crown className="w-4 h-4 mr-2" />
                Débloquer avec PRO
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const analysis = aiData?.latestTeamAnalysis;
  const hasAnalysis = analysis && analysis.globalHealthScore !== undefined;

  if (!hasAnalysis) {
    return (
      <Card className="border-2 border-webtech-yellow/30 bg-gradient-to-br from-amber-50 to-yellow-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-webtech-yellow to-amber-500 flex items-center justify-center shadow-lg">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-slate-900">IA – Commencez l'analyse</h3>
                <Badge className="bg-webtech-blue text-white text-xs">BUSINESS</Badge>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Lancez votre première analyse IA pour obtenir des recommandations personnalisées.
              </p>
              <p className="text-xs text-slate-500 italic mb-4 flex items-center gap-1">
                <Lightbulb className="w-3 h-3" />
                Les recommandations s'affineront à mesure que vous utiliserez la plateforme.
              </p>
              <Button 
                size="sm"
                className="bg-webtech-yellow hover:bg-yellow-500 text-slate-900"
                onClick={() => navigate('/ai-insights')}
              >
                <Play className="w-4 h-4 mr-2" />
                Lancer l'analyse IA
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Extract key insights from analysis
  const healthScore = analysis.globalHealthScore || 0;
  const healthStatus = getSemanticLabel('health', healthScore);
  const risks = analysis.keyRisks || [];
  const urgentActions = analysis.urgentActions || [];
  const hrKPIs = analysis.hrKPIs || {};

  // Build recommendations list
  const recommendations = [];
  
  if (risks.length > 0) {
    recommendations.push({
      type: 'warning',
      icon: AlertTriangle,
      color: 'text-amber-500',
      bg: 'bg-amber-50',
      text: `${risks.length} risque${risks.length > 1 ? 's' : ''} RH identifié${risks.length > 1 ? 's' : ''}`,
    });
  }
  
  if (hrKPIs.turnoverRisk === 'élevé' || hrKPIs.turnoverRisk === 'moyen') {
    recommendations.push({
      type: 'alert',
      icon: AlertCircle,
      color: hrKPIs.turnoverRisk === 'élevé' ? 'text-red-500' : 'text-amber-500',
      bg: hrKPIs.turnoverRisk === 'élevé' ? 'bg-red-50' : 'bg-amber-50',
      text: `Risque de turnover : ${hrKPIs.turnoverRisk}`,
    });
  }
  
  if (urgentActions.length > 0) {
    recommendations.push({
      type: 'action',
      icon: Target,
      color: 'text-blue-500',
      bg: 'bg-blue-50',
      text: `${urgentActions.length} action${urgentActions.length > 1 ? 's' : ''} urgente${urgentActions.length > 1 ? 's' : ''} à traiter`,
    });
  }

  if (healthScore < 50) {
    recommendations.push({
      type: 'health',
      icon: Activity,
      color: 'text-red-500',
      bg: 'bg-red-50',
      text: `Score santé RH faible (${healthScore}%)`,
    });
  }

  return (
    <Card className="border-2 border-webtech-yellow/30 bg-gradient-to-br from-amber-50 via-yellow-50 to-white overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-webtech-yellow/20 to-transparent rounded-bl-full" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-webtech-yellow to-amber-500 flex items-center justify-center shadow-lg">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                IA – Ce que vous devez traiter aujourd'hui
                <Badge className="bg-webtech-blue text-white text-xs">BUSINESS</Badge>
              </CardTitle>
              <CardDescription className="text-xs flex items-center gap-1">
                <span className={healthStatus.color}>{healthStatus.icon}</span>
                Score santé RH : <span className={`font-semibold ${healthStatus.color}`}>{healthScore}%</span>
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {recommendations.length > 0 ? (
          <div className="space-y-2 mb-4">
            {recommendations.slice(0, 4).map((rec, idx) => (
              <div 
                key={idx}
                className={`flex items-center gap-3 p-2.5 rounded-lg ${rec.bg} transition-all hover:scale-[1.02]`}
              >
                <rec.icon className={`w-4 h-4 ${rec.color}`} />
                <span className="text-sm text-slate-700 flex-1">{rec.text}</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 mb-4">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <span className="text-sm text-green-700">Aucune alerte urgente. Continuez comme ça ! 🎉</span>
          </div>
        )}
        
        <div className="flex gap-2">
          <Link to="/ai-insights" className="flex-1">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full border-webtech-yellow text-amber-700 hover:bg-webtech-yellow hover:text-white"
            >
              <Eye className="w-4 h-4 mr-2" />
              Recommandations
            </Button>
          </Link>
          <Link to="/executive-view" className="flex-1">
            <Button 
              size="sm" 
              className="w-full bg-webtech-blue hover:bg-webtech-blue-hover text-white"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Vue Direction
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

// Onboarding Checklist Component - Configuration adaptée selon le plan
function OnboardingChecklist({ stats, onDismiss }) {
  const { isPro, isFree } = usePlan();
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [configData, setConfigData] = useState({
    hasCampaigns: false,
    hasJobProfiles: false,
    hasSkillsLibrary: false,
    hasOrgChart: false
  });
  
  // Check localStorage for dismissed state and fetch additional config data
  useEffect(() => {
    const isDismissed = localStorage.getItem('hr_onboarding_dismissed');
    if (isDismissed === 'true') {
      setDismissed(true);
      setLoading(false);
      return; // Ne pas faire de requêtes si le guide est masqué
    }
    
    // Fetch additional configuration status seulement si le guide est visible
    const fetchConfigStatus = async () => {
      try {
        const [campaignsRes, profilesRes, skillsRes] = await Promise.all([
          api.get('/campaigns?limit=1').catch(() => ({ data: [] })),
          api.get('/skills/job-profiles?limit=1').catch(() => ({ data: [] })),
          api.get('/skills/library?limit=1').catch(() => ({ data: { skills: [] } }))
        ]);
        
        // Utiliser les stats passées en props pour éviter une requête en double
        const hasManagerAssigned = (stats?.employees || 0) > 0;
        
        setConfigData({
          hasCampaigns: (campaignsRes.data?.length || 0) > 0,
          hasJobProfiles: (profilesRes.data?.length || 0) > 0,
          hasSkillsLibrary: (skillsRes.data?.skills?.length || 0) > 0,
          hasOrgChart: hasManagerAssigned
        });
      } catch (error) {
        // Silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchConfigStatus();
  }, [stats]);

  const handleDismiss = () => {
    localStorage.setItem('hr_onboarding_dismissed', 'true');
    setDismissed(true);
  };

  if (dismissed) return null;

  // Configuration adaptée selon le plan
  const allTasks = [
    // === ÉTAPE 1: Structure de base (tous les plans) ===
    { 
      id: 'employees', 
      label: 'Ajouter vos employés',
      description: 'Importez ou créez les fiches collaborateurs',
      completed: (stats?.employees || 0) > 0,
      link: '/employees',
      icon: Users,
      category: 'base',
      priority: 1,
      requiresPro: false
    },
    { 
      id: 'departments', 
      label: 'Créer les départements',
      description: 'Organisez votre structure',
      completed: (stats?.departments || 0) > 0,
      link: '/settings?tab=departments',
      icon: Building2,
      category: 'base',
      priority: 2,
      requiresPro: false
    },
    { 
      id: 'sites', 
      label: 'Ajouter vos sites',
      description: 'Localisations de l\'entreprise',
      completed: (stats?.sites || 0) > 0,
      link: '/settings?tab=sites',
      icon: MapPin,
      category: 'base',
      priority: 3,
      requiresPro: false
    },
    { 
      id: 'managers', 
      label: 'Assigner les managers',
      description: 'Définissez la hiérarchie N+1',
      completed: configData.hasOrgChart,
      link: '/orgchart',
      icon: Network,
      category: 'base',
      priority: 4,
      requiresPro: false
    },
    // === ÉTAPE 2: Module Compétences (PRO uniquement) ===
    { 
      id: 'skills_library', 
      label: 'Créer la bibliothèque de compétences',
      description: 'Définissez les compétences clés',
      completed: configData.hasSkillsLibrary,
      link: '/skills/cockpit?tab=library',
      icon: BookOpen,
      category: 'skills',
      priority: 5,
      requiresPro: true
    },
    { 
      id: 'job_profiles', 
      label: 'Définir les profils de poste',
      description: 'Associez compétences requises par métier',
      completed: configData.hasJobProfiles,
      link: '/skills/cockpit?tab=profiles',
      icon: Briefcase,
      category: 'skills',
      priority: 6,
      requiresPro: true
    },
    // === ÉTAPE 3: Module Performance (PRO uniquement) ===
    { 
      id: 'campaign', 
      label: 'Lancer une campagne d\'évaluation',
      description: 'Démarrez le cycle de performance',
      completed: configData.hasCampaigns,
      link: '/performance?tab=campaigns',
      icon: Target,
      category: 'performance',
      priority: 7,
      requiresPro: true
    },
  ];

  // Filtrer les tâches selon le plan
  const tasks = isPro 
    ? allTasks 
    : allTasks.filter(t => !t.requiresPro);

  const completedCount = tasks.filter(t => t.completed).length;
  const pendingCount = tasks.length - completedCount;
  const progress = (completedCount / tasks.length) * 100;

  // Don't show while loading
  if (loading) return null;
  
  // Don't show if all completed - auto-hide and clear localStorage
  if (completedCount === tasks.length) {
    localStorage.removeItem('hr_onboarding_dismissed');
    return null;
  }

  return (
    <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 relative">
      {/* Notification badge for pending actions */}
      {pendingCount > 0 && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg animate-pulse">
          {pendingCount}
        </div>
      )}
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-webtech-blue to-blue-600 flex items-center justify-center shadow-lg">
              <ClipboardList className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base">Configuration initiale</CardTitle>
              <CardDescription className="text-xs">
                {completedCount}/{tasks.length} étapes complétées
                {pendingCount > 0 && (
                  <span className="ml-2 text-amber-600 font-medium">
                    • {pendingCount} action{pendingCount > 1 ? 's' : ''} en attente
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleDismiss} className="text-slate-400 hover:text-slate-600">
            Masquer
          </Button>
        </div>
        <Progress value={progress} className="h-2 mt-3" />
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {tasks.map((task) => {
            const TaskContent = (
              <div className={`
                flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer
                ${task.completed 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-white border-slate-200 hover:border-webtech-blue hover:shadow-sm'
                }
              `}>
                <div className={`
                  w-8 h-8 rounded-lg flex items-center justify-center
                  ${task.completed ? 'bg-green-100' : 'bg-slate-100'}
                `}>
                  {task.completed ? (
                    <CircleCheck className="w-5 h-5 text-green-600" />
                  ) : (
                    <task.icon className="w-4 h-4 text-slate-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium truncate ${task.completed ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                      {task.label}
                    </p>
                    {task.requiresPro && !isPro && (
                      <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">BUSINESS</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{task.description}</p>
                </div>
                {!task.completed && <ChevronRight className="w-4 h-4 text-slate-400" />}
              </div>
            );

            // Pour les tâches PRO avec un plan gratuit, montrer un lien vers upgrade
            if (task.requiresPro && !isPro) {
              return (
                <Link key={task.id} to="/billing/pricing">
                  {TaskContent}
                </Link>
              );
            }

            return (
              <Link key={task.id} to={task.link}>
                {TaskContent}
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { isPro, isFree, loading: planLoading, canAccessAI, refreshPlan } = usePlan();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [upgradeModal, setUpgradeModal] = useState({ open: false, feature: '' });
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Mettre à jour l'onglet si le paramètre URL change
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['overview', 'kpi'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Redirect SuperAdmin without company to admin page
  if (user?.role === 'SuperAdmin' && !user?.companyId) {
    return <Navigate to="/admin" replace />;
  }
  
  // Attendre que le plan soit chargé avant de rediriger les employés/managers
  if (planLoading && (user?.role === 'Employé' || user?.role === 'Manager')) {
    return (
      <DashboardLayout title="Chargement...">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }
  
  // Redirect Employés to their dedicated dashboard (PRO only)
  // For FREE companies, employees shouldn't have accounts, but if they do, EmployeeDashboardPage handles access control
  if (user?.role === 'Employé') {
    return <Navigate to="/my-dashboard" replace />;
  }
  
  // Redirect Managers to their dedicated team dashboard
  // Managers have their own cockpit for team management
  if (user?.role === 'Manager') {
    return <Navigate to="/manager-dashboard" replace />;
  }

  useEffect(() => {
    if (user?.companyId) {
      fetchData();
      checkOnboarding();
      // Rafraîchir le plan au chargement pour détecter les changements
      refreshPlan();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, aiRes, activitiesRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/ai/dashboard').catch(() => ({ data: null })),
        api.get('/audit/recent?limit=5').catch(() => ({ data: { activity: [] } }))
      ]);
      setStats(statsRes.data);
      setAiData(aiRes.data);
      setRecentActivities(activitiesRes.data?.activity || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };
  
  // Check if onboarding should be shown
  const checkOnboarding = async () => {
    // Vérifier d'abord le localStorage pour éviter une requête API inutile
    const dismissedUntil = localStorage.getItem('hr_onboarding_wizard_dismissed');
    if (dismissedUntil) {
      const dismissedDate = new Date(dismissedUntil);
      // Ne pas afficher si fermé il y a moins de 7 jours
      if (dismissedDate > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) {
        return; // Pas de requête API nécessaire
      }
    }
    
    try {
      const response = await api.get('/onboarding/should-show');
      if (response.data?.shouldShow) {
        setTimeout(() => setShowOnboarding(true), 1500); // Show after page loads
      }
    } catch (error) {
      // Silently fail - onboarding is optional
    }
  };

  const handleProAction = (feature, action) => {
    if (isFree) {
      setUpgradeModal({ open: true, feature });
    } else {
      action();
    }
  };

  // Quick Actions - RH oriented
  const quickActions = [
    { 
      label: 'Lancer une évaluation', 
      description: 'Créer une campagne', 
      icon: Target, 
      link: '/performance',
      color: 'bg-amber-500',
      pro: true
    },
    { 
      label: 'Analyser les compétences', 
      description: 'Cartographier les talents', 
      icon: Brain, 
      link: '/skills',
      color: 'bg-purple-500',
      pro: true
    },
    { 
      label: 'Voir recommandations IA', 
      description: 'Actions prioritaires', 
      icon: Sparkles, 
      link: '/ai-insights',
      color: 'bg-webtech-yellow',
      pro: true
    },
    { 
      label: 'Exporter rapport DG', 
      description: 'Synthèse direction', 
      icon: FileBarChart, 
      link: '/ai-insights',
      color: 'bg-blue-600',
      pro: true
    },
    { 
      label: 'Nouvel employé', 
      description: 'Ajouter un collaborateur', 
      icon: UserPlus, 
      link: '/employees/new',
      color: 'bg-green-500',
      pro: false
    },
    { 
      label: 'Import CSV', 
      description: 'Import en masse', 
      icon: Upload, 
      link: '/import',
      color: 'bg-slate-500',
      pro: false
    },
  ];

  // Stats with semantic labels
  const statCards = [
    { 
      label: 'Employés', 
      value: stats?.employees || 0, 
      icon: Users, 
      color: 'from-blue-500 to-blue-600',
      semantic: getSemanticLabel('employees', stats?.employees || 0),
      link: '/employees',
    },
    { 
      label: 'Départements', 
      value: stats?.departments || 0, 
      icon: Building2, 
      color: 'from-emerald-500 to-emerald-600',
      semantic: stats?.departments > 0 
        ? { label: 'Configuré ✓', color: 'text-green-600', bg: 'bg-green-50' }
        : { label: 'À créer', color: 'text-amber-600', bg: 'bg-amber-50' },
      link: '/settings',
    },
    { 
      label: 'Sites', 
      value: stats?.sites || 0, 
      icon: MapPin, 
      color: 'from-amber-500 to-amber-600',
      semantic: stats?.sites > 0 
        ? { label: 'Configuré ✓', color: 'text-green-600', bg: 'bg-green-50' }
        : { label: 'À créer', color: 'text-amber-600', bg: 'bg-amber-50' },
      link: '/settings',
    },
    { 
      label: 'Utilisateurs', 
      value: stats?.users || 0, 
      icon: UserCog, 
      color: 'from-purple-500 to-purple-600',
      semantic: getSemanticLabel('coverage', 100),
      link: '/settings',
    },
  ];

  const modules = [
    {
      name: 'HR People Mapping™',
      description: 'Gestion des employés et organigramme',
      icon: Users,
      status: 'active',
      link: '/employees',
      color: 'bg-blue-500',
      pro: false
    },
    {
      name: 'HR Performance System™',
      description: 'Évaluations annuelles',
      icon: Star,
      status: isPro ? 'active' : 'pro',
      link: '/performance',
      color: 'bg-amber-500',
      pro: true
    },
    {
      name: 'HR Skills Intelligence™',
      description: 'Gestion des compétences',
      icon: Brain,
      status: isPro ? 'active' : 'pro',
      link: '/skills',
      color: 'bg-purple-500',
      pro: true
    }
  ];

  return (
    <DashboardLayout title="Tableau de bord">
      <TooltipProvider>
        <div className="space-y-6" data-testid="dashboard-page">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Welcome Message */}
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                {getGreeting()}, <span className="text-webtech-blue">{user?.firstName}</span>
              </h1>
              <p className="text-slate-500 mt-1 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {getDayName()}, {formatDate()}
                {isPro && (
                  <Badge className="bg-gradient-to-r from-webtech-yellow to-amber-500 text-white text-xs ml-2">
                    <Crown className="w-3 h-3 mr-1" />
                    PRO
                  </Badge>
                )}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="hidden md:flex">
                <Share2 className="h-4 w-4 mr-2" />
                Partager
              </Button>
              <Button variant="outline" size="sm" className="hidden md:flex">
                <Printer className="h-4 w-4 mr-2" />
                Imprimer
              </Button>
              <Button size="sm" className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
                <Download className="h-4 w-4 mr-2" />
                Exporter
              </Button>
            </div>
          </div>

          {/* Tabs Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-slate-100 p-1">
              <TabsTrigger value="overview" className="data-[state=active]:bg-white">
                Vue d'ensemble
              </TabsTrigger>
              <TabsTrigger value="kpi" className="data-[state=active]:bg-white">
                📊 Indicateurs RH
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-white">
                Analytiques
              </TabsTrigger>
              <TabsTrigger value="modules" className="data-[state=active]:bg-white">
                Modules
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-6 space-y-6">
              {/* Onboarding Checklist */}
              <OnboardingChecklist stats={stats} />

              {/* Stats Grid with Semantic Labels */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {loading ? (
                  Array(4).fill(0).map((_, i) => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-6">
                        <div className="h-24 bg-slate-200 rounded"></div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  statCards.map((stat) => (
                    <Link key={stat.label} to={stat.link}>
                      <Card className="hover:shadow-lg transition-all cursor-pointer group border-0 shadow-sm overflow-hidden">
                        <CardContent className="p-0">
                          <div className="p-5">
                            <div className="flex items-start justify-between mb-4">
                              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                                <stat.icon className="w-6 h-6 text-white" />
                              </div>
                              {/* Semantic Badge */}
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${stat.semantic.bg} ${stat.semantic.color}`}>
                                {stat.semantic.label}
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                            <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                          </div>
                          <div className={`h-1 w-full bg-gradient-to-r ${stat.color}`}></div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))
                )}
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Quick Actions - RH Oriented */}
                <Card className="lg:col-span-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Actions RH rapides</CardTitle>
                        <CardDescription>Ce que vous pouvez faire maintenant</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {quickActions.map((action) => {
                        const isLocked = action.pro && isFree;
                        
                        return (
                          <Tooltip key={action.label}>
                            <TooltipTrigger asChild>
                              <div
                                onClick={() => {
                                  if (isLocked) {
                                    setUpgradeModal({ open: true, feature: action.label });
                                  } else {
                                    navigate(action.link);
                                  }
                                }}
                                className={`
                                  group p-4 rounded-xl border transition-all cursor-pointer relative
                                  ${isLocked 
                                    ? 'border-slate-200 bg-slate-50 opacity-75 hover:opacity-100' 
                                    : 'border-slate-200 hover:border-webtech-blue hover:shadow-md'
                                  }
                                `}
                              >
                                {isLocked && (
                                  <div className="absolute top-2 right-2">
                                    <Lock className="w-3.5 h-3.5 text-slate-400" />
                                  </div>
                                )}
                                <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center mb-3 ${isLocked ? '' : 'group-hover:scale-110'} transition-transform`}>
                                  <action.icon className="w-5 h-5 text-white" />
                                </div>
                                <h3 className={`font-medium text-sm ${isLocked ? 'text-slate-500' : 'text-slate-900 group-hover:text-webtech-blue'} transition-colors`}>
                                  {action.label}
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">{action.description}</p>
                                {action.pro && (
                                  <Badge className="mt-2 bg-webtech-yellow/10 text-amber-700 text-[10px]">
                                    PRO
                                  </Badge>
                                )}
                              </div>
                            </TooltipTrigger>
                            {isLocked && (
                              <TooltipContent>
                                <p>Disponible avec le plan PRO</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Status Summary */}
                <Card className="bg-gradient-to-br from-webtech-blue to-blue-700 text-white border-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-white/90">Résumé</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <p className="text-white/70 text-sm">Total Employés</p>
                        <p className="text-4xl font-bold">{stats?.employees || 0}</p>
                        <p className="text-white/60 text-xs mt-1">
                          {getSemanticLabel('employees', stats?.employees || 0).label}
                        </p>
                      </div>
                      
                      <div className="h-px bg-white/20"></div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-white/70 text-sm">Couverture</span>
                          <span className="text-white font-medium flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4" />
                            Complet
                          </span>
                        </div>
                        <Progress value={100} className="h-2 bg-white/20" />
                      </div>
                      
                      <div className="pt-2">
                        <Link to="/employees">
                          <Button variant="secondary" size="sm" className="w-full bg-white/10 hover:bg-white/20 text-white border-white/20">
                            Voir les détails
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Bottom Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Activity / Recent Items */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Activity className="h-5 w-5 text-webtech-blue" />
                        Activité récente
                      </CardTitle>
                      <Link to="/audit">
                        <Button variant="ghost" size="sm" className="text-webtech-blue">
                          Voir tout
                        </Button>
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {recentActivities.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">Aucune activité récente</p>
                      ) : (
                        recentActivities.map((activity, idx) => {
                          // Mapper les catégories aux icônes et couleurs
                          const getActivityDisplay = (cat, action) => {
                            const mapping = {
                              'employee.created': { icon: UserPlus, color: 'text-green-500', label: 'Employé ajouté' },
                              'employee.updated': { icon: Users, color: 'text-blue-500', label: 'Employé modifié' },
                              'employee.deleted': { icon: Users, color: 'text-red-500', label: 'Employé supprimé' },
                              'user.created': { icon: CheckCircle2, color: 'text-blue-500', label: 'Compte créé' },
                              'user.updated': { icon: UserCog, color: 'text-blue-500', label: 'Compte modifié' },
                              'campaign.created': { icon: Target, color: 'text-purple-500', label: 'Campagne créée' },
                              'campaign.activated': { icon: Target, color: 'text-green-500', label: 'Campagne activée' },
                              'campaign.closed': { icon: Target, color: 'text-slate-500', label: 'Campagne clôturée' },
                              'auth.login': { icon: CheckCircle2, color: 'text-green-500', label: 'Connexion' },
                              'auth.logout': { icon: AlertCircle, color: 'text-slate-500', label: 'Déconnexion' },
                              'objective.created': { icon: Target, color: 'text-amber-500', label: 'Objectif créé' },
                              'evaluation.submitted': { icon: FileText, color: 'text-blue-500', label: 'Évaluation soumise' },
                              'data.imported': { icon: Upload, color: 'text-green-500', label: 'Import effectué' },
                              'data.exported': { icon: Download, color: 'text-blue-500', label: 'Export effectué' },
                            };
                            return mapping[cat] || { icon: Activity, color: 'text-slate-500', label: action || 'Action' };
                          };
                          
                          const display = getActivityDisplay(activity.category, activity.action);
                          const IconComponent = display.icon;
                          
                          // Formater le temps relatif
                          const formatRelativeTime = (dateStr) => {
                            if (!dateStr) return '';
                            const date = new Date(dateStr);
                            const now = new Date();
                            const diffMs = now - date;
                            const diffMins = Math.floor(diffMs / 60000);
                            const diffHours = Math.floor(diffMs / 3600000);
                            const diffDays = Math.floor(diffMs / 86400000);
                            
                            if (diffMins < 1) return 'À l\'instant';
                            if (diffMins < 60) return `Il y a ${diffMins}min`;
                            if (diffHours < 24) return `Il y a ${diffHours}h`;
                            if (diffDays < 7) return `Il y a ${diffDays}j`;
                            return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                          };
                          
                          return (
                            <div key={activity.id || idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                              <div className={`w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center ${display.color}`}>
                                <IconComponent className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{display.label}</p>
                                <p className="text-xs text-slate-500 truncate">{activity.targetName || activity.userName || '-'}</p>
                              </div>
                              <span className="text-xs text-slate-400 whitespace-nowrap">{formatRelativeTime(activity.createdAt)}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Stats / Health Overview */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <PieChart className="h-5 w-5 text-webtech-blue" />
                        Indicateurs clés
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Department distribution */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-600">Par département</span>
                          <span className="text-sm font-medium text-slate-900">{stats?.departments || 0} depts</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden flex">
                          <div className="h-full bg-blue-500" style={{ width: '40%' }}></div>
                          <div className="h-full bg-emerald-500" style={{ width: '25%' }}></div>
                          <div className="h-full bg-amber-500" style={{ width: '20%' }}></div>
                          <div className="h-full bg-purple-500" style={{ width: '15%' }}></div>
                        </div>
                      </div>
                      
                      {/* Health Score - PRO Feature */}
                      <div className={`p-3 rounded-lg ${isPro ? 'bg-gradient-to-r from-green-50 to-emerald-50' : 'bg-slate-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-600 flex items-center gap-2">
                            Score santé RH
                            {!isPro && <Lock className="w-3 h-3 text-slate-400" />}
                          </span>
                          {isPro ? (
                            <span className={`text-sm font-bold ${getSemanticLabel('health', aiData?.latestTeamAnalysis?.globalHealthScore || 0).color}`}>
                              {aiData?.latestTeamAnalysis?.globalHealthScore || 0}%
                            </span>
                          ) : (
                            <Badge className="bg-webtech-yellow/10 text-amber-700 text-xs">BUSINESS</Badge>
                          )}
                        </div>
                        {isPro && (
                          <Progress 
                            value={aiData?.latestTeamAnalysis?.globalHealthScore || 0} 
                            className="h-2 bg-slate-200"
                          />
                        )}
                      </div>

                      {/* Legend */}
                      <div className="flex flex-wrap gap-3 pt-2">
                        {[
                          { color: 'bg-blue-500', label: 'IT' },
                          { color: 'bg-emerald-500', label: 'RH' },
                          { color: 'bg-amber-500', label: 'Finance' },
                          { color: 'bg-purple-500', label: 'Autres' },
                        ].map((item) => (
                          <div key={item.label} className="flex items-center gap-1.5">
                            <div className={`w-2.5 h-2.5 rounded-full ${item.color}`}></div>
                            <span className="text-xs text-slate-500">{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* KPI RH Tab */}
            <TabsContent value="kpi" className="mt-6">
              <KPIDashboard />
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="mt-6">
              {isPro ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-webtech-blue" />
                        Tableau de bord analytique
                      </CardTitle>
                      <CardDescription>
                        Visualisez les tendances et indicateurs de performance
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center py-12">
                      <LineChart className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Analyses en cours de développement</h3>
                      <p className="text-slate-500 max-w-md mx-auto">
                        Les graphiques avancés et visualisations seront disponibles très prochainement.
                      </p>
                      <Link to="/ai-insights">
                        <Button className="mt-4 bg-webtech-blue hover:bg-webtech-blue-hover">
                          <Brain className="w-4 h-4 mr-2" />
                          Voir les insights IA
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card className="py-12">
                  <CardContent className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                      <Lock className="h-8 w-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Analytiques PRO</h3>
                    <p className="text-slate-500 max-w-md mx-auto mb-4">
                      Accédez aux graphiques avancés, tendances et analyses détaillées avec le plan PRO.
                    </p>
                    <Button 
                      className="bg-gradient-to-r from-webtech-yellow to-amber-500 text-white hover:from-yellow-500 hover:to-amber-600"
                      onClick={() => navigate('/billing/subscription')}
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Passer au PRO
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Modules Tab */}
            <TabsContent value="modules" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {modules.map((module) => {
                  const isLocked = module.pro && isFree;
                  
                  return (
                    <div
                      key={module.name}
                      onClick={() => {
                        if (isLocked) {
                          setUpgradeModal({ open: true, feature: module.name });
                        } else {
                          navigate(module.link);
                        }
                      }}
                      className="cursor-pointer"
                    >
                      <Card className={`hover:shadow-lg transition-all h-full ${isLocked ? 'opacity-75 hover:opacity-100' : ''}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 ${module.color} rounded-xl flex items-center justify-center ${isLocked ? '' : 'group-hover:scale-110'} transition-transform relative`}>
                              <module.icon className="w-6 h-6 text-white" />
                              {isLocked && (
                                <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow">
                                  <Lock className="w-3 h-3 text-slate-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <h3 className={`font-semibold ${isLocked ? 'text-slate-500' : 'text-slate-900'}`}>
                                  {module.name}
                                </h3>
                                {isLocked ? (
                                  <Badge className="bg-webtech-yellow/10 text-amber-700 text-xs">BUSINESS</Badge>
                                ) : (
                                  <Badge className="bg-green-100 text-green-700 text-xs">Actif</Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">{module.description}</p>
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t flex items-center justify-between">
                            <span className="text-xs text-slate-400">
                              {isLocked ? 'Cliquez pour débloquer' : 'Accéder au module'}
                            </span>
                            <ArrowRight className={`w-4 h-4 ${isLocked ? 'text-slate-300' : 'text-slate-400'}`} />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>

          {/* PRO Upgrade Modal */}
          <ProUpgradeModal 
            isOpen={upgradeModal.open}
            onClose={() => setUpgradeModal({ open: false, feature: '' })}
            feature={upgradeModal.feature}
          />
          
          {/* Onboarding Wizard */}
          <OnboardingWizard 
            isOpen={showOnboarding}
            onClose={() => {
              setShowOnboarding(false);
              // Mémoriser que l'utilisateur a fermé la modal
              localStorage.setItem('hr_onboarding_wizard_dismissed', new Date().toISOString());
            }}
          />
        </div>
      </TooltipProvider>
    </DashboardLayout>
  );
}
